import { StyleSheet, View } from "@react-pdf/renderer";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { PdfCard } from "src/core/components/folders-list/pdf/PdfCard";
import { CompletionIcon } from "src/core/components/pdf/icons/CompletionIcon";
import { RenewalIcon } from "src/core/components/pdf/icons/RenewalIcon";
import { ShieldDangerIcon } from "src/core/components/pdf/icons/ShieldDangerIcon";
import { ShieldOkIcon } from "src/core/components/pdf/icons/ShieldOk";
import { ValidatedIcon } from "src/core/components/pdf/icons/ValidatedIcon";
import { ValidatedFoldersStatistics } from "src/core/hooks/folders-list/folder-risk-statistics/types";

const styles = StyleSheet.create({
  cardsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    paddingHorizontal: 10,
    marginTop: 10,
    marginBottom: 6,
  },

  cardWrapper: {
    marginRight: 12,
    marginBottom: 12,
  },

  cardWrapperEndOfRow: {
    marginRight: 0,
    marginBottom: 12,
  },
});

type CardData = {
  headerBackgroundColor: string;
  icon: ReactNode;
  title: string;
  subTitle: string;
  textColor: string;
  counter: string;
  line1: string;
  line2: string;
};

/**
 * Détermine si l'index correspond à la fin d'une ligne (3 colonnes).
 * Séparé pour clarifier l'intention et faciliter l'évolution (ex: 4 colonnes).
 */
const isEndOfGridRow = (index: number, columns: number) => (index + 1) % columns === 0;

/**
 * Retourne le style du wrapper de carte sans jamais produire `undefined`.
 * => évite les erreurs TS strictes sur `@react-pdf/renderer`.
 */
const getCardWrapperStyle = (index: number) => {
  return isEndOfGridRow(index, 3) ? styles.cardWrapperEndOfRow : styles.cardWrapper;
};

/**
 * Affiche les cartes sous forme de grille (3 colonnes max).
 */
export const CardsGrid = ({ statistics }: { statistics: ValidatedFoldersStatistics }) => {
  const { t } = useTranslation();

  const cards: CardData[] = [
    {
      headerBackgroundColor: "#B9BFE7",
      icon: <ShieldDangerIcon />,
      title: t(
        "pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.dossiersSensibles"
      ),
      subTitle: t(
        "pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.examenPepSanction"
      ),
      textColor: "#877ABB",
      counter: statistics.dashboardCounts.validationPendingHr.toString(),
      line1: t(
        "pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.dossiersSensibles"
      ),
      line2: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.aValider"),
    },
    {
      headerBackgroundColor: "#F6A890",
      icon: <ShieldOkIcon />,
      title: t(
        "pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.validationDossiers"
      ),
      subTitle: t(
        "pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.examenLabClient"
      ),
      textColor: "#F38360",
      counter: statistics.dashboardCounts.validationPending.toString(),
      line1: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.dossiersClient"),
      line2: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.aValider"),
    },
    {
      headerBackgroundColor: "#B1C7D0",
      icon: <CompletionIcon />,
      title: t(
        "pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.completionDossiers"
      ),
      subTitle: t(
        "pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.examenLabClient"
      ),
      textColor: "#6690A3",
      counter: statistics.dashboardCounts.draft.toString(),
      line1: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.dossiersClient"),
      line2: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.aCompleter"),
    },
    {
      headerBackgroundColor: "#F7D4AF",
      icon: <RenewalIcon />,
      title: t(
        "pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.maintienMissions"
      ),
      subTitle: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.majDossiers"),
      textColor: "#F3AC60",
      counter: statistics.dashboardCounts.renewal.toString(),
      line1: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.missionsClient"),
      line2: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.aReactualiser"),
    },
    {
      headerBackgroundColor: "#AFD1AD",
      icon: <ValidatedIcon />,
      title: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.dossiersValides"),
      subTitle: t(
        "pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.missionsAcceptees"
      ),
      textColor: "#8DBE89",
      counter: statistics.dashboardCounts.validated.toString(),
      line1: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.dossiersClient"),
      line2: t("pdf.validatedFolders.sections.table.repartitionVigilanceRenforcee.traites"),
    },
  ];

  return (
    <View style={styles.cardsGrid}>
      {cards.map((card, index) => (
        <View key={`${card.title}-${index}`} style={getCardWrapperStyle(index)}>
          <PdfCard {...card} />
        </View>
      ))}
    </View>
  );
};
